import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createOptionIdentifier,
  type OptionListItem,
  type SortDirection,
} from '@/types/optionDiscovery';
import { useNavigation, type AppPage } from '@hooks/useNavigation';
import { useOptionsData } from '@contexts/OptionsDataContext';
import { usePrices } from '@hooks/usePrices';
import { orderService } from '@services/orderService';
import { calculateOptionPriceAtPoint } from '@services/optionDiscoveryService';
import { volatilityService } from '@services/volatilityService';
import type { Asset } from '@shared/types';
import { generateDynamicStrikePrices } from '@utils/chartHelpers';
import { calculateLambda } from '@utils/leverage';

import { OptionListItemRow } from './options/OptionListItemRow';
import { Header } from './shared/Header';

interface OptionsHomeScreenProps {
  onNavigate: (page: AppPage) => void;
}

type SortType = 'performance' | 'strike' | 'leverage' | 'traders';

const getAssetIconPath = (asset: Asset): string => {
  const assetLower = asset.toLowerCase();
  return `/icons/${assetLower}-icon.svg`;
};

// Generate stable random trader count (1-999) based on option identifier
const getTraderCount = (option: OptionListItem): number => {
  // Create a stable seed from option properties
  const seed = `${option.asset}-${option.strikePrice}-${option.type}-${option.expiryTimestamp}`;
  
  // Simple hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use absolute value and modulo to get 1-999
  return Math.abs(hash % 999) + 1;
};

const ASSET_FILTER_STORAGE_KEY = 'yv_options_asset_filter_v1';

export function OptionsHomeScreen({ onNavigate }: OptionsHomeScreenProps) {
  const { selectOption } = useNavigation();
  const [assetFilter, setAssetFilter] = useState<Asset>(() => {
    // Restore asset filter from localStorage, defaulting to SOL
    try {
      const stored = localStorage.getItem(ASSET_FILTER_STORAGE_KEY);
      if (stored && (stored === 'BTC' || stored === 'ETH' || stored === 'SOL')) {
        return stored as Asset;
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'SOL';
  });
  const [sortType, setSortType] = useState<SortType>('strike');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const highestPerformanceRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);
  const userHasScrolledRef = useRef(false);
  const lastAssetFilterRef = useRef<Asset | null>(null);

  const { options: allOptions, isLoading } = useOptionsData();
  const pricesQuery = usePrices();
  const livePrices = pricesQuery.data;
  
  // Track price updates to force re-sort when leverage sort is active
  // Extract actual price values so React detects changes even if Map reference is stable
  const priceUpdateKey = useMemo(() => {
    if (sortType !== 'leverage' || !livePrices) return '';
    // Create a key from all asset prices to detect any price change
    const prices: string[] = [];
    livePrices.forEach((value, asset) => {
      if (value?.price) {
        prices.push(`${asset}:${value.price.toFixed(4)}`);
      }
    });
    return prices.sort().join('|');
  }, [livePrices, sortType]);

  const filterOptions: Array<{ value: Asset }> = [
    { value: 'BTC' },
    { value: 'ETH' },
    { value: 'SOL' },
  ];

  // Get current spot price for the selected asset
  const currentSpotPrice = useMemo(() => {
    const livePrice = livePrices?.get(assetFilter)?.price;
    if (livePrice && livePrice > 0) {
      return livePrice;
    }
    // Fallback to spot price from first option if available
    const firstOption = allOptions.find(opt => opt.asset === assetFilter);
    return firstOption?.spotPrice ?? 0;
  }, [livePrices, assetFilter, allOptions]);

  // Helper function to get trader count (stable random number based on option identifier)
  const getTraderCount = useCallback((option: OptionListItem): number => {
    const seed = `${option.asset}-${option.strikePrice}-${option.type}-${option.expiryTimestamp}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash % 999) + 1;
  }, []);

  // Helper function to calculate leverage (Lambda = Delta × Spot Price / Option Price)
  // Uses real-time prices to match what's displayed in OptionListItemRow
  const calculateLeverage = useCallback((option: OptionListItem): number => {
    // Use live spot price if available, otherwise use the option's own spot price
    const livePrice = livePrices?.get(option.asset)?.price;
    const spotPrice = (livePrice && livePrice > 0) ? livePrice : option.spotPrice;
    
    // Ensure we have valid spot price
    if (spotPrice <= 0) {
      return 0;
    }
    
    // Calculate real-time option price (same as OptionListItemRow)
    const currentVolatility = volatilityService.getVolatility(option.asset);
    let optionPrice = option.currentPrice; // Fallback to cached price
    
    if (currentVolatility && currentVolatility > 0) {
      const realTimePrice = calculateOptionPriceAtPoint(
        option,
        spotPrice,
        currentVolatility,
        Date.now(),
      );
      optionPrice = realTimePrice > 0 ? realTimePrice : option.currentPrice;
    }
    
    // Ensure we have valid option price
    if (optionPrice <= 0) {
      return 0;
    }
    
    const lambda = calculateLambda({
      option,
      spotPrice,
      optionPrice,
      volatility: currentVolatility ?? undefined,
    });
    return lambda ?? 0;
  }, [livePrices]);

  // Filter by asset, limit to 10 strikes above and below current price, and apply sorting
  const options = useMemo(() => {
    // Filter by asset
    let filtered = allOptions.filter(opt => opt.asset === assetFilter);

    // Limit to 10 strikes above and 10 strikes below current price (20 total)
    // Use generated strikes (what should exist) rather than what's in allOptions
    // This ensures we include strikes even if some options failed to calculate
    if (currentSpotPrice > 0 && filtered.length > 0) {
      // Get the generated strikes for this asset (what should exist)
      const generatedStrikes = generateDynamicStrikePrices(currentSpotPrice, assetFilter);
      
      // Find strikes above and below current price from generated strikes
      const strikesAbove = generatedStrikes.filter(strike => strike > currentSpotPrice);
      const strikesBelow = generatedStrikes.filter(strike => strike < currentSpotPrice);
      
      // Find the nearest strike to current price (could be above or below)
      let nearestStrike: number | null = null;
      let nearestDistance = Infinity;
      for (const strike of generatedStrikes) {
        const distance = Math.abs(strike - currentSpotPrice);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestStrike = strike;
        }
      }
      
      // Take closest 10 above and 10 below
      const selectedStrikesAbove = strikesAbove.slice(0, 10);
      const selectedStrikesBelow = strikesBelow.slice(-10);
      
      // Include strikes at current price (if any)
      const strikesAtPrice = generatedStrikes.filter(strike => Math.abs(strike - currentSpotPrice) < 0.01);
      
      // Combine all selected strikes, ensuring nearest strike is always included
      const allowedStrikesSet = new Set([
        ...selectedStrikesBelow,
        ...strikesAtPrice,
        ...selectedStrikesAbove,
      ]);
      
      // Always include the nearest strike if we found one
      if (nearestStrike !== null) {
        allowedStrikesSet.add(nearestStrike);
      }
      
      // Convert to array and ensure we have exactly 20 strikes
      // If we have more than 20, remove the most OTM strike (furthest from current price)
      let allowedStrikes = Array.from(allowedStrikesSet);
      if (allowedStrikes.length > 20) {
        // Sort by distance from current price, remove the furthest one
        allowedStrikes.sort((a, b) => {
          const distA = Math.abs(a - currentSpotPrice);
          const distB = Math.abs(b - currentSpotPrice);
          return distA - distB; // Closest first
        });
        // Keep only the 20 closest strikes
        allowedStrikes = allowedStrikes.slice(0, 20);
      }
      
      const allowedStrikesSetFinal = new Set(allowedStrikes);
      
      // Filter options to only include those with allowed strike prices
      filtered = filtered.filter(opt => allowedStrikesSetFinal.has(opt.strikePrice));
    }

    // Apply sorting based on sortType
    const sorted = [...filtered].sort((a, b) => {
      if (sortType === 'strike') {
        const diff = a.strikePrice - b.strikePrice;
        return sortDirection === 'desc' ? -diff : diff;
      } else if (sortType === 'leverage') {
        const leverageA = calculateLeverage(a);
        const leverageB = calculateLeverage(b);
        const diff = leverageA - leverageB;
        return sortDirection === 'desc' ? -diff : diff;
      } else if (sortType === 'traders') {
        const tradersA = getTraderCount(a);
        const tradersB = getTraderCount(b);
        const diff = tradersA - tradersB;
        return sortDirection === 'desc' ? -diff : diff;
      } else {
        // performance sorting (default)
        const diff = a.priceChangePercent - b.priceChangePercent;
        return sortDirection === 'desc' ? -diff : diff;
      }
    });

    return sorted;
  }, [allOptions, assetFilter, currentSpotPrice, sortType, sortDirection, livePrices, calculateLeverage, getTraderCount, priceUpdateKey]);

  // Find the option with highest performance
  const highestPerformanceOption = useMemo(() => {
    if (options.length === 0) return null;
    return options.reduce((best, current) => 
      current.priceChangePercent > best.priceChangePercent ? current : best
    );
  }, [options]);

  // Track user scroll to prevent auto-scroll from overriding manual scrolling
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Mark that user has manually scrolled
      userHasScrolledRef.current = true;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to highest performance option only on initial load or when asset filter changes
  useEffect(() => {
    // Reset auto-scroll flag when asset filter changes
    const assetFilterChanged = lastAssetFilterRef.current !== assetFilter;
    if (assetFilterChanged) {
      hasAutoScrolledRef.current = false;
      userHasScrolledRef.current = false;
      lastAssetFilterRef.current = assetFilter;
    }

    // Only auto-scroll if:
    // 1. We have a highest performance option
    // 2. We haven't auto-scrolled yet (or asset filter changed)
    // 3. User hasn't manually scrolled
    if (
      !highestPerformanceOption || 
      !scrollContainerRef.current || 
      !highestPerformanceRef.current ||
      hasAutoScrolledRef.current ||
      userHasScrolledRef.current
    ) {
      return;
    }

    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      if (highestPerformanceRef.current && scrollContainerRef.current && !userHasScrolledRef.current) {
        const container = scrollContainerRef.current;
        const target = highestPerformanceRef.current;
        
        // Calculate scroll position to center the target element
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const targetTop = targetRect.top - containerRect.top + scrollTop;
        const containerHeight = containerRect.height;
        const targetHeight = targetRect.height;
        
        // Center the target element in the viewport
        const scrollPosition = targetTop - (containerHeight / 2) + (targetHeight / 2);
        
        container.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });

        // Mark that we've auto-scrolled
        hasAutoScrolledRef.current = true;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [highestPerformanceOption, assetFilter]);

  const handleOptionClick = (option: OptionListItem) => {
    const identifier = createOptionIdentifier(
      option.asset,
      option.strikePrice,
      option.type,
      option.expiryTimestamp,
    );
    selectOption(identifier);
  };

  const handleBuy = (option: OptionListItem) => {
    orderService.createOrder({
      asset: option.asset,
      type: option.type,
      strikePrice: option.strikePrice,
      quantity: 1,
      orderType: 'MARKET',
      side: 'BUY',
      expiryTimestamp: option.expiryTimestamp,
    });
  };

  const toggleSortDirection = () => {
    setSortDirection((prev: SortDirection) =>
      prev === 'desc' ? 'asc' : 'desc',
    );
  };

  const handleSortTypeClick = (type: SortType) => {
    if (sortType === type) {
      toggleSortDirection();
    } else {
      setSortType(type);
      setSortDirection('desc');
    }
  };

  return (
    <div
      className="bg-brand-dark h-full w-full flex flex-col overflow-x-hidden"
      style={{ backgroundColor: 'var(--brand-dark)' }}
    >
      {/* Header */}
      <Header asset="OPTIONS" onNavigate={onNavigate} showCountdown={true} />

      {/* Asset Filter */}
      <div className="flex gap-3 w-full py-2 px-2 bg-brand-dark">
        {filterOptions.map(option => (
          <motion.button
            key={option.value}
            className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center ${
              assetFilter === option.value
                ? 'bg-white/20'
                : 'bg-white/10 hover:bg-white/10'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setAssetFilter(option.value);
              // Persist asset filter to localStorage
              try {
                localStorage.setItem(ASSET_FILTER_STORAGE_KEY, option.value);
              } catch {
                // Ignore localStorage errors
              }
            }}
          >
            <img
              src={getAssetIconPath(option.value)}
              alt={option.value}
              className="w-6 h-6 object-contain transition-all"
              style={{
                filter: assetFilter === option.value ? 'none' : 'grayscale(100%)',
                opacity: assetFilter === option.value ? 1 : 0.6,
              }}
            />
          </motion.button>
        ))}
      </div>

      {/* Sort Header */}
      <div className="relative grid items-center gap-2 px-2 py-2 overflow-x-hidden" style={{ gridTemplateColumns: '110px 0.3fr 0.3fr 0.4fr auto' }}>
        {/* Price sort - aligned above strike prices */}
        <div>
        <motion.button
            className={`flex items-center gap-1 text-xs transition-colors whitespace-nowrap ${
              sortType === 'strike'
                ? 'text-white/80'
                : 'text-white/60 hover:text-white/80'
            }`}
            onClick={() => handleSortTypeClick('strike')}
            whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          >
            <span>Target Price</span>
            {sortType === 'strike' && (
              <FontAwesomeIcon
                icon={sortDirection === 'desc' ? faArrowDown : faArrowUp}
                className="text-xs"
              />
            )}
        </motion.button>
        </div>
        
        {/* Leverage column header */}
        <div className="flex justify-end">
        <motion.button
            className={`flex items-center gap-1 text-xs transition-colors whitespace-nowrap ${
              sortType === 'leverage'
                ? 'text-white/80'
                : 'text-white/60 hover:text-white/80'
            }`}
            onClick={() => handleSortTypeClick('leverage')}
            whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          >
            <span>Leverage</span>
            {sortType === 'leverage' && (
              <FontAwesomeIcon
                icon={sortDirection === 'desc' ? faArrowDown : faArrowUp}
                className="text-xs"
              />
            )}
        </motion.button>
      </div>

        {/* Traders column header */}
        <div className="flex justify-end">
          <motion.button
            className={`flex items-center gap-1 text-xs transition-colors whitespace-nowrap ${
              sortType === 'traders'
                ? 'text-white/80'
                : 'text-white/60 hover:text-white/80'
            }`}
            onClick={() => handleSortTypeClick('traders')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Traders</span>
            {sortType === 'traders' && (
              <FontAwesomeIcon
                icon={sortDirection === 'desc' ? faArrowDown : faArrowUp}
                className="text-xs"
              />
            )}
          </motion.button>
        </div>
        
        {/* Middle: Performance sort - aligned with percentages */}
        <div className="flex justify-end">
        <motion.button
            className={`flex items-center gap-1 text-xs transition-colors whitespace-nowrap ${
              sortType === 'performance'
                ? 'text-white/80'
                : 'text-white/60 hover:text-white/80'
            }`}
            onClick={() => handleSortTypeClick('performance')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span>Performance</span>
                {sortType === 'performance' && (
          <FontAwesomeIcon
            icon={sortDirection === 'desc' ? faArrowDown : faArrowUp}
            className="text-xs"
          />
                )}
        </motion.button>
        </div>
        
        {/* Right: Arrow placeholder */}
        <div className="w-8 flex-shrink-0" />
      </div>

      {/* Options List */}
      <div className="flex-1 overflow-hidden relative">
        <div 
          ref={scrollContainerRef}
          className="overflow-y-auto overflow-x-hidden h-full pb-16 px-2"
        >
          {isLoading && options.length === 0 ? (
            <div className="flex items-center justify-center h-[200px]">
              <div className="text-white/40 text-sm">Loading options...</div>
            </div>
          ) : options.length === 0 ? (
            <div className="flex items-center justify-center h-[200px]">
              <div className="text-center">
                <div className="text-white/40 text-sm mb-2">
                  No options available
                </div>
                <div className="text-white/20 text-xs">
                  No {assetFilter} options found
                </div>
              </div>
            </div>
          ) : (
            <>
            <div className="space-y-2">
              {options.map((option, index) => {
                const isHighestPerformance = highestPerformanceOption && 
                  option.asset === highestPerformanceOption.asset &&
                  option.strikePrice === highestPerformanceOption.strikePrice &&
                  option.type === highestPerformanceOption.type &&
                  option.expiryTimestamp === highestPerformanceOption.expiryTimestamp;
                
                return (
                  <div
                    key={`${option.asset}-${option.strikePrice}-${option.type}`}
                    ref={isHighestPerformance ? highestPerformanceRef : undefined}
                  >
                <OptionListItemRow
                  option={option}
                  index={index}
                  onClick={() => handleOptionClick(option)}
                  onBuy={() => handleBuy(option)}
                />
                  </div>
                );
              })}
            </div>
              {/* Options count - at bottom of scrollable content */}
              <div className="px-4 py-3 mt-4 border-t border-white/10">
                <span className="text-white/40 text-xs">
                  {options.length} option{options.length !== 1 ? 's' : ''}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Bottom gradient */}
        {options.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
}
